/**
 * Canonical admin sidebar IA.
 * Nested children deep-link into page tabs via `?tab=`.
 * Keep in sync with App.tsx routes and page tab keys.
 */

export type AdminNavLeaf = { name: string; href: string };
export type AdminNavItem = { name: string; href: string; children?: AdminNavLeaf[] };
export type AdminNavGroup = { id: string; label: string; items: AdminNavItem[] };

/** Default `?tab=` when the href omits it (used for active-state matching). */
export const ADMIN_DEFAULT_TAB_BY_PATH: Record<string, string> = {
  '/admin/clubs': 'queue',
  '/admin/shops': 'queue',
  '/admin/passport': 'flags',
  '/admin/payments': 'summary',
  '/admin/email': 'status',
};

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ name: 'Platform overview', href: '/admin' }],
  },
  {
    id: 'community',
    label: 'Community',
    items: [
      { name: 'Users', href: '/admin/users' },
      {
        name: 'Clubs',
        href: '/admin/clubs',
        children: [
          { name: 'Review queue', href: '/admin/clubs' },
          { name: 'Directory', href: '/admin/clubs?tab=directory' },
        ],
      },
      {
        name: 'Shops',
        href: '/admin/shops',
        children: [
          { name: 'Request queue', href: '/admin/shops' },
          { name: 'Directory', href: '/admin/shops?tab=directory' },
          { name: 'Contributor disputes', href: '/admin/shops?tab=contributors' },
        ],
      },
      { name: 'Certifications', href: '/admin/certifications' },
      { name: 'Performance', href: '/admin/performance' },
      { name: 'Badges', href: '/admin/badges' },
      { name: 'Limited designations', href: '/admin/limited-designations' },
      {
        name: 'Bilt Passport',
        href: '/admin/passport',
        children: [
          { name: 'Rollout flags', href: '/admin/passport' },
          { name: 'Review queue', href: '/admin/passport?tab=queue' },
          { name: 'All encounters', href: '/admin/passport?tab=encounters' },
          { name: 'Health', href: '/admin/passport?tab=health' },
        ],
      },
    ],
  },
  {
    id: 'events',
    label: 'Events & payments',
    items: [
      { name: 'Events', href: '/admin/events' },
      {
        name: 'Payments',
        href: '/admin/payments',
        children: [
          { name: 'Summary', href: '/admin/payments' },
          { name: 'Accounts', href: '/admin/payments?tab=accounts' },
          { name: 'Orders', href: '/admin/payments?tab=orders' },
          { name: 'Disputes', href: '/admin/payments?tab=disputes' },
          { name: 'Webhooks', href: '/admin/payments?tab=webhooks' },
          { name: 'Reconcile', href: '/admin/payments?tab=reconcile' },
          { name: 'Flags', href: '/admin/payments?tab=flags' },
        ],
      },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      { name: 'Featured Builds', href: '/admin/featured' },
      { name: 'Sponsorship', href: '/admin/sponsorship' },
      { name: 'Achievements', href: '/admin/achievements' },
      { name: 'Factory specs', href: '/admin/factory' },
      { name: 'Build media', href: '/admin/media' },
      { name: 'Product updates', href: '/admin/updates' },
      { name: 'Testimonials', href: '/admin/testimonials' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    items: [
      { name: 'Invite codes', href: '/admin/codes' },
      { name: 'Privacy requests', href: '/admin/privacy' },
      { name: 'Feature flags', href: '/admin/feature-flags' },
      {
        name: 'Email',
        href: '/admin/email',
        children: [
          { name: 'Provider status', href: '/admin/email' },
          { name: 'Flags', href: '/admin/email?tab=flags' },
          { name: 'Delivery log', href: '/admin/email?tab=delivery' },
          { name: 'Templates', href: '/admin/email?tab=previews' },
        ],
      },
      { name: 'AI settings', href: '/admin/ai' },
      { name: 'Activity log', href: '/admin/activity' },
    ],
  },
];

export function splitAdminHref(href: string): { pathname: string; tab: string | null } {
  const [pathname, query = ''] = href.split('?');
  const tab = new URLSearchParams(query).get('tab');
  return { pathname, tab };
}

export function currentAdminTabForPath(pathname: string, search: string): string | null {
  const fromUrl = new URLSearchParams(search).get('tab');
  if (fromUrl) return fromUrl;
  return ADMIN_DEFAULT_TAB_BY_PATH[pathname] ?? null;
}

export function isAdminHrefActive(href: string, pathname: string, search: string): boolean {
  const target = splitAdminHref(href);
  if (target.pathname === '/admin') {
    return pathname === '/admin';
  }
  if (pathname !== target.pathname) return false;

  const defaultTab = ADMIN_DEFAULT_TAB_BY_PATH[target.pathname];
  if (!defaultTab) return true;

  const currentTab = currentAdminTabForPath(pathname, search);
  const expectedTab = target.tab ?? defaultTab;
  return currentTab === expectedTab;
}

export function adminPathInGroup(group: AdminNavGroup, pathname: string): boolean {
  return group.items.some((item) => {
    const itemPath = splitAdminHref(item.href).pathname;
    if (itemPath === '/admin') return pathname === '/admin';
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
  });
}
